/**
 * Profile Canvas Drawing Utilities
 * 
 * This module contains all the drawing functions for rendering elevation profiles
 * on a 2D canvas. It handles gridlines, data points, axis labels, and coordinate
 * transformations for the profile visualization.
 */

export const CANVAS_CONSTANTS = {
  MARGINS: { L: 68, R: 16, T: 12, B: 34 },
  MAX_TICKS: 6,
  // Visual size (radius in pixels) for plotted points
  POINT_SIZE: 2,
  CAMERA_HEIGHT_OFFSET: 2000,
  MIN_RANGE_DIFF: 1e-3,
  ZOOM: {
    MIN_SCALE: 1,
    MAX_SCALE: 20,
    SENSITIVITY: 0.001
  }
};

/**
 * Scans a Float32Array of V coordinates and returns clamped min/max.
 * @param {Float32Array} vCoordinates - The V coordinate array
 * @param {number} count - Number of elements to scan
 * @returns {{ vMin: number, vMax: number }}
 */
export function computeVRange(vCoordinates, count) {
  let vMin = Infinity, vMax = -Infinity;
  for (let i = 0; i < count; i++) {
    const val = vCoordinates[i];
    if (isFinite(val)) {
      if (val < vMin) vMin = val;
      if (val > vMax) vMax = val;
    }
  }
  if (!isFinite(vMin)) { vMin = -1; vMax = 1; }
  if (vMax - vMin < CANVAS_CONSTANTS.MIN_RANGE_DIFF) { vMax = vMin + 1; }
  return { vMin, vMax };
}

/**
 * Calculates a "nice" step size for axis ticks based on data range
 * @param {number} range - The data range to create ticks for
 * @param {number} maxTicks - Maximum number of ticks desired
 * @returns {number} A nice step size for axis ticks
 */
export function calculateNiceStep(range, maxTicks = CANVAS_CONSTANTS.MAX_TICKS) {
  if (!isFinite(range) || range <= 0) return 1;
  
  const roughStep = range / maxTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalizedRough = roughStep / magnitude;
  const stepBases = [1, 2, 5, 10];
  
  let bestStep = stepBases[0];
  let minDifference = Math.abs(stepBases[0] - normalizedRough);
  
  for (const base of stepBases) {
    const difference = Math.abs(base - normalizedRough);
    if (difference < minDifference) {
      bestStep = base;
      minDifference = difference;
    }
  }
  
  return bestStep * magnitude;
}

/**
 * Draws the background for the plot area
 * @param {CanvasRenderingContext2D} context - Canvas 2D rendering context
 * @param {number} plotWidth - Plot area width in pixels
 * @param {number} plotHeight - Plot area height in pixels
 */
export function drawBackground(context, plotWidth, plotHeight) {
  context.fillStyle = 'rgba(0,0,0,0.45)';
  context.fillRect(CANVAS_CONSTANTS.MARGINS.L, CANVAS_CONSTANTS.MARGINS.T, plotWidth, plotHeight);
}

/**
 * Draws gridlines for the profile plot
 * @param {CanvasRenderingContext2D} context - Canvas 2D rendering context
 * @param {number} canvasWidth - Canvas width in pixels
 * @param {number} canvasHeight - Canvas height in pixels
 * @param {number} uMin - Minimum U coordinate value
 * @param {number} uMax - Maximum U coordinate value
 * @param {number} vMin - Minimum V coordinate value
 * @param {number} vMax - Maximum V coordinate value
 * @param {Function} uToX - Function to convert U coordinates to X pixels
 * @param {Function} vToY - Function to convert V coordinates to Y pixels
 * @returns {Object} Grid step information {uStep, vStep, uStart, vStart}
 */
export function drawGridlines(context, canvasWidth, canvasHeight, uMin, uMax, vMin, vMax, uToX, vToY) {
  const uStep = calculateNiceStep(uMax - uMin);
  const vStep = calculateNiceStep(vMax - vMin);
  const uStart = Math.ceil(uMin / uStep) * uStep;
  const vStart = (Math.ceil(vMin / vStep) - 1) * vStep;

  context.save();
  context.strokeStyle = '#666';
  context.lineWidth = 1;
  context.globalAlpha = 0.35;

  // Draw vertical gridlines (U axis)
  for (let u = uStart; u <= uMax + 1e-6; u += uStep) {
    const x = uToX(u);
    if (x < CANVAS_CONSTANTS.MARGINS.L - 1 || x > canvasWidth - CANVAS_CONSTANTS.MARGINS.R + 1) continue;
    context.beginPath();
    context.moveTo(x, CANVAS_CONSTANTS.MARGINS.T);
    context.lineTo(x, canvasHeight - CANVAS_CONSTANTS.MARGINS.B);
    context.stroke();
  }

  // Draw horizontal gridlines (V axis)
  for (let v = vStart; v <= vMax + vStep + 1e-6; v += vStep) {
    const y = vToY(v);
    if (y < CANVAS_CONSTANTS.MARGINS.T - 1 || y > canvasHeight - CANVAS_CONSTANTS.MARGINS.B + 1) continue;
    context.beginPath();
    context.moveTo(CANVAS_CONSTANTS.MARGINS.L, y);
    context.lineTo(canvasWidth - CANVAS_CONSTANTS.MARGINS.R, y);
    context.stroke();
  }

  context.restore();
  return { uStep, vStep, uStart, vStart };
}

/**
 * Draws individual data points with their colors
 * @param {CanvasRenderingContext2D} context - Canvas 2D rendering context
 * @param {Float32Array} uCoordinates - U coordinate data array
 * @param {Float32Array} vCoordinates - V coordinate data array
 * @param {Uint8Array} rgbColors - RGB color data array
 * @param {number} pointCount - Number of points to draw
 * @param {number} uMin - Minimum U coordinate for clipping
 * @param {number} uMax - Maximum U coordinate for clipping
 * @param {number} canvasWidth - Canvas width in pixels
 * @param {number} canvasHeight - Canvas height in pixels
 * @param {Function} uToX - Function to convert U coordinates to X pixels
 * @param {Function} vToY - Function to convert V coordinates to Y pixels
 */
export function drawDataPoints(context, uCoordinates, vCoordinates, rgbColors, pointCount, uMin, uMax, canvasWidth, canvasHeight, uToX, vToY) {
  if (!uCoordinates || !vCoordinates || !rgbColors || !pointCount) return;

  for (let i = 0; i < pointCount; i++) {
    const u = uCoordinates[i];
    if (u < uMin || u > uMax) continue;

    const x = uToX(u);
    const y = vToY(vCoordinates[i]);

    // Clip points outside plot area
    if (x < CANVAS_CONSTANTS.MARGINS.L || x >= canvasWidth - CANVAS_CONSTANTS.MARGINS.R || 
        y < CANVAS_CONSTANTS.MARGINS.T || y >= canvasHeight - CANVAS_CONSTANTS.MARGINS.B) continue;

    const colorIndex = i * 3;
    context.fillStyle = `rgb(${rgbColors[colorIndex]},${rgbColors[colorIndex + 1]},${rgbColors[colorIndex + 2]})`;
    context.beginPath();
    context.arc(x, y, CANVAS_CONSTANTS.POINT_SIZE, 0, Math.PI * 2);
    context.fill();
  }
}

/**
 * Draws tick labels on both axes
 * @param {CanvasRenderingContext2D} context - Canvas 2D rendering context
 * @param {number} canvasWidth - Canvas width in pixels
 * @param {number} canvasHeight - Canvas height in pixels
 * @param {number} uMax - Maximum U coordinate value
 * @param {number} vMax - Maximum V coordinate value
 * @param {number} uStep - Step size for U axis ticks
 * @param {number} vStep - Step size for V axis ticks
 * @param {number} uStart - Starting U value for ticks
 * @param {number} vStart - Starting V value for ticks
 * @param {Function} uToX - Function to convert U coordinates to X pixels
 * @param {Function} vToY - Function to convert V coordinates to Y pixels
 */
export function drawTickLabels(context, canvasWidth, canvasHeight, uMax, vMax, uStep, vStep, uStart, vStart, uToX, vToY) {
  context.fillStyle = '#ccc';
  context.font = '14px sans-serif';

  // Draw U axis labels (bottom)
  context.textAlign = 'center';
  context.textBaseline = 'top';
  for (let u = uStart; u <= uMax + 1e-6; u += uStep) {
    const x = uToX(u);
    if (x < CANVAS_CONSTANTS.MARGINS.L - 1 || x > canvasWidth - CANVAS_CONSTANTS.MARGINS.R + 1) continue;
    const label = u.toFixed(uStep < 1 ? 1 : 0);
    context.fillText(label, x, canvasHeight - CANVAS_CONSTANTS.MARGINS.B + 4);
  }

  // Draw V axis labels (left)
  context.textAlign = 'right';
  context.textBaseline = 'middle';
  for (let v = vStart; v <= vMax + vStep + 1e-6; v += vStep) {
    const y = vToY(v);
    if (y < CANVAS_CONSTANTS.MARGINS.T - 1 || y > canvasHeight - CANVAS_CONSTANTS.MARGINS.B + 1) continue;
    let decimals = 0;
    if (vStep < 1) decimals = 2;
    else if (vStep < 10) decimals = 1;
    const label = v.toFixed(decimals);
    context.fillText(label, CANVAS_CONSTANTS.MARGINS.L - 6, y);
  }
}

/**
 * Draws axis labels (Distance and Elevation)
 * @param {CanvasRenderingContext2D} context - Canvas 2D rendering context
 * @param {number} canvasWidth - Canvas width in pixels
 * @param {number} canvasHeight - Canvas height in pixels
 * @param {number} plotHeight - Plot area height in pixels
 * @param {Function} t - Translation function for internationalization
 */
export function drawAxisLabels(context, canvasWidth, canvasHeight, plotHeight, t) {
  context.fillStyle = '#ccc';
  context.font = '16px sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  // Bottom axis label (Distance)
  const bottomLabelY = canvasHeight - CANVAS_CONSTANTS.MARGINS.B / 2 + 12;
  context.fillText(`${t('Distance')} (m)`, canvasWidth / 2, bottomLabelY);

  // Left axis label (Elevation) - rotated 90 degrees
  context.save();
  context.translate(CANVAS_CONSTANTS.MARGINS.L - 54, CANVAS_CONSTANTS.MARGINS.T + plotHeight / 2);
  context.rotate(-Math.PI / 2);
  context.fillText(`${t('Elevation')} (m)`, 0, 0);
  context.restore();
}

/**
 * Main drawing function that orchestrates all the drawing operations
 * @param {HTMLCanvasElement} canvas - The canvas element to draw on
 * @param {Object} basisRef - Reference to the current profile basis
 * @param {Object} coloredRef - Reference to the current point data
 * @param {Object} transformRef - Reference to the current view transform
 * @param {Object} metricsRef - Reference to plot metrics
 * @param {Object} centerPendingRef - Reference to centering state
 * @param {Function} t - Translation function
 * @param {Function} debugLog - Debug logging function
 * @returns {boolean} True if drawing was successful, false otherwise
 */
export function drawProfile(canvas, basisRef, coloredRef, transformRef, metricsRef, centerPendingRef, t, debugLog) {
  if (!canvas) return false;

  const context = canvas.getContext('2d');
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  context.clearRect(0, 0, canvasWidth, canvasHeight);

  const currentBasis = basisRef.current;
  if (!currentBasis) return false;

  const { u: uCoordinates, v: vCoordinates, rgb: rgbColors, n: pointCount } = coloredRef.current || {};
  if (!uCoordinates || !vCoordinates || !pointCount) return false;

  // Calculate data ranges
  const uMin = 0;
  const uMax = currentBasis.length;
  const { vMin, vMax } = computeVRange(vCoordinates, pointCount);

  // Calculate plot dimensions and scaling
  const plotWidth = Math.max(1, canvasWidth - CANVAS_CONSTANTS.MARGINS.L - CANVAS_CONSTANTS.MARGINS.R);
  const plotHeight = Math.max(1, canvasHeight - CANVAS_CONSTANTS.MARGINS.T - CANVAS_CONSTANTS.MARGINS.B);
  const unitScale = Math.min(plotWidth / (uMax - uMin), plotHeight / (vMax - vMin));

  // Auto-center while streaming: keep re-centering on every draw until the
  // user explicitly interacts (pan/zoom).  The flag is cleared by interaction
  // handlers in ProfileViewer, not here, so incremental tile arrivals that
  // shift vMin/vMax always produce a centered view.
  if (centerPendingRef.current && pointCount > 0) {
    const transform = transformRef.current;
    const scaledUnit = transform.scale * unitScale;
    const contentWidth = scaledUnit * (uMax - uMin);
    const contentHeight = scaledUnit * (vMax - vMin);
    transform.dx = (plotWidth - contentWidth) / 2;
    transform.dy = -(plotHeight - contentHeight) / 2;
    debugLog('auto-center', { contentWidth, contentHeight, dx: transform.dx, dy: transform.dy });
  }

  // Create coordinate transformation functions
  const { scale, dx, dy } = transformRef.current;
  const finalScale = scale * unitScale;
  const uToX = u => CANVAS_CONSTANTS.MARGINS.L + dx + finalScale * (u - uMin);
  const vToY = v => (canvasHeight - CANVAS_CONSTANTS.MARGINS.B) + dy - finalScale * (v - vMin);

  // Save metrics for pan/zoom operations
  metricsRef.current = { 
    plotW: plotWidth, 
    plotH: plotHeight, 
    umin: uMin, 
    umax: uMax, 
    vmin: vMin, 
    vmax: vMax, 
    unitScale 
  };

  // Draw all components
  drawBackground(context, plotWidth, plotHeight);
  
  const { uStep, vStep, uStart, vStart } = drawGridlines(context, canvasWidth, canvasHeight, uMin, uMax, vMin, vMax, uToX, vToY);
  
  drawDataPoints(context, uCoordinates, vCoordinates, rgbColors, pointCount, uMin, uMax, canvasWidth, canvasHeight, uToX, vToY);
  
  drawTickLabels(context, canvasWidth, canvasHeight, uMax, vMax, uStep, vStep, uStart, vStart, uToX, vToY);
  
  drawAxisLabels(context, canvasWidth, canvasHeight, plotHeight, t);

  debugLog('drawProfile', { 
    count: pointCount, 
    uMin, 
    uMax, 
    vMin, 
    vMax, 
    canvasWidth, 
    canvasHeight, 
    scale: transformRef.current.scale, 
    dx: transformRef.current.dx, 
    dy: transformRef.current.dy 
  });

  return true;
}
